/**
 * FlyoutFooter — shared footer bar for flyout drawers.
 *
 * Provides: left-side delete button + right-side cancel/save buttons.
 * Extracted from V2Users, V2Roles, V2Sites, V2Views, V2Connectors,
 * and V2SummaryTemplates to eliminate ~50 duplicated lines per page.
 */

import { Box } from "@mui/material";
import { cancelBtnSx, saveBtnSx, deleteBtnSx } from "./crudStyles";
import { TrashIcon } from "./icons";

// ---------------------------------------------------------------------------
// FlyoutFooter
// ---------------------------------------------------------------------------

interface FlyoutFooterProps {
  readonly onCancel: () => void;
  readonly cancelLabel: string;
  /** Primary save action */
  readonly onSave?: () => void;
  readonly saveLabel?: string;
  readonly isSaving?: boolean;
  /** Delete action (shows on left side) */
  readonly onDelete?: () => void;
  readonly deleteLabel?: string;
  /** Whether to show the delete button */
  readonly showDelete?: boolean;
  /** Additional save buttons (e.g. Sites has two save buttons) */
  readonly extraSaveButtons?: React.ReactNode;
}

export default function FlyoutFooter({
  onCancel,
  cancelLabel,
  onSave,
  saveLabel,
  isSaving = false,
  onDelete,
  deleteLabel,
  showDelete = false,
  extraSaveButtons,
}: FlyoutFooterProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 20px",
        borderTop: "1px solid var(--border-subtle)",
        flexShrink: 0,
      }}
    >
      {/* Left side: Delete button */}
      <Box>
        {showDelete && onDelete && deleteLabel && (
          <Box
            component="button"
            onClick={onDelete}
            sx={deleteBtnSx}
          >
            <TrashIcon />
            {deleteLabel}
          </Box>
        )}
      </Box>

      {/* Right side: Cancel + Save */}
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Box
          component="button"
          onClick={onCancel}
          sx={cancelBtnSx}
        >
          {cancelLabel}
        </Box>
        {onSave && saveLabel && (
          <Box
            component="button"
            onClick={onSave}
            disabled={isSaving}
            sx={saveBtnSx}
          >
            {saveLabel}
          </Box>
        )}
        {extraSaveButtons}
      </Box>
    </Box>
  );
}
